import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { timeout } from 'rxjs';

import { AjsResponse, wrapAjs } from '../ajs-response';
import { buildParams } from '../http-params.util';

/**
 * EventApiService (Angular 18) — event instance summaries, schedules,
 * statements, config, and coupon price lists.
 *
 * Replaces apps/b2b-ui/common/services/api/eventApi/eventApi.service.js.
 *
 * Three sub-namespaces:
 *   apiService.event.config.list(configId)
 *   apiService.event.statements.{list,search}
 *   apiService.event.coupon.priceLists
 *
 * Legacy contract preserved 1:1:
 *   - List/search/cancelled/exceptions/etc. carry a 60s timeout from the
 *     legacy `timeout: 60000` `$http` config. We use rxjs `timeout(60000)`.
 *   - `list(props)` reads `props.cache` to forward to the legacy `$http`
 *     cache. Angular HttpClient has no equivalent in-memory cache; the
 *     `cache` field is silently ignored (the underlying responses are still
 *     served, just without the legacy in-memory dedupe).
 *   - `gantt()` and `schedules()` used `$.ajax` (jQuery) in the legacy,
 *     bypassing Angular's HTTP interceptors. We move them to `HttpClient`,
 *     which DOES go through interceptors. Behaviour change: requests now
 *     carry CSRF / auth headers like every other endpoint.
 *   - `schedules()` URL is `/v1/api/event/instance` (singular — the legacy
 *     `this.service.substring(0, length - 1)` trimmed the trailing 's').
 *   - `createOffSchedule` and `createFromPrivateSchedule` differ only in
 *     `offSchedule: true|false` (and one extra field `salesChannels` on the
 *     private-schedule branch); both payload shapes preserved verbatim.
 *   - `updateOffSchedule` mutates `props.offSchedule = true` (side effect
 *     preserved).
 *   - Dates from `Date` instances are converted to ISO-8601 with local
 *     timezone offset (`yyyy-MM-ddTHH:mm:ss+HHMM`) — matches AngularJS'
 *     `$filter('date')(d, 'yyyy-MM-ddTHH:mm:ssZ')` legacy behaviour. NOT
 *     UTC: do not substitute `Date.prototype.toISOString()` (which forces UTC).
 */
const EVENT_INSTANCES_BASE_V1 = '/v1/api/event/instances';
const EVENT_INSTANCES_BASE_V2 = '/v2/api/event/instances';
const EVENT_INSTANCE_SINGULAR_BASE_V2 = '/v2/api/event/instance';
const EVENT_INSTANCES_COUPON_BASE_V2 = '/v2/api/event/instances/coupon';
const EVENT_INSTANCES_CONFIG_BASE_V2 = '/v2/api/event/instances/config';
const EVENT_INSTANCES_STATEMENTS_BASE_V2 = '/v2/api/event/instances/statements';

const LEGACY_TIMEOUT_MS = 60000;

const EVENT_LIST_FIELDS = [
  'startTime',
  'saleChannel',
  'endTime',
  'pageSize',
  'page',
  'sortField',
  'templateId',
  'sortDirection',
  'search',
  'resourceRequestFilter',
  'filter',
] as const;

const EVENT_CANCELED_FIELDS = [
  'startTime',
  'endTime',
  'pageSize',
  'page',
  'sortField',
  'sortDirection',
  'search',
] as const;

const EVENT_EXCEPTIONS_FIELDS = ['pageSize', 'page', 'sortField', 'sortDirection', 'search'] as const;

const EVENT_STATEMENTS_LIST_FIELDS = [
  'startTime',
  'saleChannel',
  'endTime',
  'pageSize',
  'page',
  'sortField',
  'templateId',
  'sortDirection',
  'search',
  'resourceRequestFilter',
  'filter',
  'type',
] as const;

const EVENT_COUPON_PRICELISTS_FIELDS = ['eventIds', 'validate'] as const;

/**
 * Format a Date (or string passthrough) as `yyyy-MM-ddTHH:mm:ss+HHMM` in the
 * caller's local timezone. Matches the legacy `$filter('date')(date, 'Z')`
 * format used by `CalendarService.toISO8601`.
 */
function toISO8601Local(date: Date | string | number): string {
  if (typeof date === 'string') return date;
  const d = new Date(date);
  const pad = (n: number) => String(n).padStart(2, '0');
  const yyyy = d.getFullYear();
  const MM = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const HH = pad(d.getHours());
  const mm = pad(d.getMinutes());
  const ss = pad(d.getSeconds());
  const offsetMinutes = -d.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const tzh = pad(Math.floor(Math.abs(offsetMinutes) / 60));
  const tzm = pad(Math.abs(offsetMinutes) % 60);
  return `${yyyy}-${MM}-${dd}T${HH}:${mm}:${ss}${sign}${tzh}${tzm}`;
}

interface EventListProps {
  startTime?: string;
  saleChannel?: string;
  endTime?: string;
  pageSize?: number;
  page?: number;
  sortField?: string;
  templateId?: number | string;
  sortDirection?: string;
  search?: string;
  resourceRequestFilter?: unknown;
  filter?: unknown;
  cache?: unknown;
  [key: string]: unknown;
}

interface EventSearchProps extends EventListProps {
  customFields?: unknown;
  states?: unknown;
}

interface OffSchedulePayload {
  eventId?: number | string;
  id?: number | string;
  scheduleId?: number | string;
  originalFields?: unknown;
  name?: string;
  description?: string;
  shortDescription?: string;
  colorCategory?: string;
  emailConfirmationMessage?: string;
  emailCancellationMessage?: string;
  capacity?: number;
  startTime?: unknown;
  endTime?: unknown;
  flexPays?: unknown;
  resources?: unknown;
  resourceDetails?: unknown;
  priceList?: unknown;
  rebookingFee?: unknown;
  ticketConfig?: unknown;
  customFields?: unknown;
  orderRequestId?: number | string;
  organization?: unknown;
  salesChannels?: unknown;
  offSchedule?: boolean;
}

function buildOffSchedulePayload(props: OffSchedulePayload, offSchedule: boolean) {
  return {
    id: props.eventId,
    templateId: props.id,
    scheduleId: props.scheduleId,
    originalFields: props.originalFields,
    name: props.name,
    description: props.description,
    shortDescription: props.shortDescription,
    colorCategory: props.colorCategory,
    emailConfirmationMessage: props.emailConfirmationMessage,
    emailCancellationMessage: props.emailCancellationMessage,
    capacity: props.capacity,
    startTime: props.startTime,
    endTime: props.endTime,
    flexPays: props.flexPays,
    resources: props.resources,
    resourceDetails: props.resourceDetails,
    priceList: props.priceList,
    rebookingFee: props.rebookingFee,
    ticketConfig: props.ticketConfig,
    customFields: props.customFields,
    offSchedule,
    orderRequestId: props.orderRequestId,
    organization: props.organization,
    ...(offSchedule ? {} : { salesChannels: props.salesChannels }),
  };
}

@Injectable({ providedIn: 'root' })
export class EventApiService {
  private readonly http = inject(HttpClient);

  list(props: EventListProps): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http
        .get<unknown>(`${EVENT_INSTANCES_BASE_V2}/summaries`, {
          params: buildParams(props, EVENT_LIST_FIELDS),
        })
        .pipe(timeout(LEGACY_TIMEOUT_MS)),
    );
  }

  getEventsByTemplateId(templateId: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http
        .get<unknown>(`${EVENT_INSTANCES_BASE_V2}/excerpts`, {
          params: buildParams({ templateId }),
        })
        .pipe(timeout(LEGACY_TIMEOUT_MS)),
    );
  }

  search(props: EventSearchProps): Promise<AjsResponse<unknown>> {
    const pagination = {
      page: props.page,
      pageSize: props.pageSize,
      sortDirection: props.sortDirection,
      sortField: props.sortField,
    };
    return wrapAjs(
      this.http
        .post<unknown>(`${EVENT_INSTANCES_BASE_V2}/summaries`, {
          startTime: props.startTime,
          endTime: props.endTime,
          templateId: props.templateId,
          saleChannel: props.saleChannel,
          resourceRequestFilter: props.resourceRequestFilter,
          filter: props.filter,
          search: props.search,
          pagination,
          customFields: props.customFields,
          states: props.states,
        })
        .pipe(timeout(LEGACY_TIMEOUT_MS)),
    );
  }

  detail(
    id: number | string,
    props?: { membershipId?: number | string; membershipCategoryId?: number | string },
  ): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(`${EVENT_INSTANCES_BASE_V2}/${id}`, {
        params: buildParams(props ?? {}, ['membershipId', 'membershipCategoryId']),
      }),
    );
  }

  canceled(
    startTime: Date | string | undefined,
    endTime: Date | string | undefined,
    props: Record<string, unknown>,
  ): Promise<AjsResponse<unknown>> {
    const merged: Record<string, unknown> = { ...props };
    if (startTime) merged['startTime'] = toISO8601Local(startTime);
    if (endTime) merged['endTime'] = toISO8601Local(endTime);
    return wrapAjs(
      this.http
        .get<unknown>(`${EVENT_INSTANCES_BASE_V2}/cancelled`, {
          params: buildParams(merged, EVENT_CANCELED_FIELDS),
        })
        .pipe(timeout(LEGACY_TIMEOUT_MS)),
    );
  }

  gantt(startTime?: Date | string, endTime?: Date | string): Promise<AjsResponse<unknown>> {
    const params: Record<string, unknown> = {};
    if (startTime) params['startTime'] = toISO8601Local(startTime);
    if (endTime) params['endTime'] = toISO8601Local(endTime);
    return wrapAjs(
      this.http
        .get<unknown>(`${EVENT_INSTANCES_BASE_V2}/gantt`, { params: buildParams(params) })
        .pipe(timeout(LEGACY_TIMEOUT_MS)),
    );
  }

  schedules(props: {
    templateId?: number | string;
    startTime?: Date | string;
    endTime?: Date | string;
  }): Promise<AjsResponse<unknown>> {
    const params: Record<string, unknown> = {};
    if (props.templateId) params['templateId'] = props.templateId;
    if (props.startTime) params['startTime'] = toISO8601Local(props.startTime);
    if (props.endTime) params['endTime'] = toISO8601Local(props.endTime);
    return wrapAjs(
      this.http
        .get<unknown>(EVENT_INSTANCE_SINGULAR_BASE_V2, { params: buildParams(params) })
        .pipe(timeout(LEGACY_TIMEOUT_MS)),
    );
  }

  exceptions(eventTemplateId: number | string, props?: Record<string, unknown>): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http
        .get<unknown>(`${EVENT_INSTANCES_BASE_V2}/${eventTemplateId}/diff`, {
          params: buildParams(props, EVENT_EXCEPTIONS_FIELDS),
        })
        .pipe(timeout(LEGACY_TIMEOUT_MS)),
    );
  }

  createOffSchedule(props: OffSchedulePayload): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.post<unknown>(EVENT_INSTANCES_BASE_V2, buildOffSchedulePayload(props, true)));
  }

  createFromPrivateSchedule(props: OffSchedulePayload): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.post<unknown>(EVENT_INSTANCES_BASE_V2, buildOffSchedulePayload(props, false)));
  }

  // Legacy mutates `props.offSchedule = true`. Preserved.
  updateOffSchedule(props: OffSchedulePayload): Promise<AjsResponse<unknown>> {
    props.offSchedule = true;
    return wrapAjs(this.http.put<unknown>(EVENT_INSTANCES_BASE_V2, props));
  }

  removeOffSchedule(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.delete<unknown>(`${EVENT_INSTANCES_BASE_V2}/${id}`));
  }

  readonly config = {
    list: (configId: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${EVENT_INSTANCES_CONFIG_BASE_V2}/${configId}`)),
  };

  readonly statements = {
    list: (props: EventListProps): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http
          .get<unknown>(EVENT_INSTANCES_STATEMENTS_BASE_V2, {
            params: buildParams(props, EVENT_STATEMENTS_LIST_FIELDS),
          })
          .pipe(timeout(LEGACY_TIMEOUT_MS)),
      ),

    search: (props: EventSearchProps & { type?: string }): Promise<AjsResponse<unknown>> => {
      const pagination = {
        page: props.page,
        pageSize: props.pageSize,
        sortDirection: props.sortDirection,
        sortField: props.sortField,
      };
      return wrapAjs(
        this.http
          .post<unknown>(EVENT_INSTANCES_STATEMENTS_BASE_V2, {
            startTime: props.startTime,
            saleChannel: props.saleChannel,
            endTime: props.endTime,
            pagination,
            templateId: props.templateId,
            search: props.search,
            resourceRequestFilter: props.resourceRequestFilter,
            filter: props.filter,
            type: props.type,
            customFields: props.customFields,
            states: props.states,
          })
          .pipe(timeout(LEGACY_TIMEOUT_MS)),
      );
    },
  };

  readonly coupon = {
    priceLists: (props: { eventIds: string; validate?: string }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(`${EVENT_INSTANCES_COUPON_BASE_V2}/all/pricelists`, {
          params: buildParams(props, EVENT_COUPON_PRICELISTS_FIELDS),
        }),
      ),
  };
}
